import axios from "axios";
import {ONTOLOGIES, SCHEMA} from "./endpoints";
import {GenerateSchemaModel} from "../components/schema/generate-schema/GenerateSchemaStep";
import BaseService from "./base-service";
import * as SomlUtils from "../utils/soml-utils";
import {Schema} from "../types/Schema";

/**
 * Service for uploading ontologies and generating SOML schemas from them.
 */
export default class OntologyService extends BaseService {

  /**
   * Uploads the given ontology files to the workbench server.
   *
   * @param {Array<File>} files the files to upload
   * @param {(event: ProgressEvent) => void} progressCallback a callback function to report the upload progress
   * @returns {Promise<any>} promise resolving when the upload is complete with the uploaded files
   */
  static uploadOntologies(files: Array<File>, progressCallback: (event: ProgressEvent) => void): Promise<any> {
    const data = new FormData();
    files.forEach(file => data.append('files', file));
    return axios.post(ONTOLOGIES, data, {
      onUploadProgress: progressEvent => progressCallback(progressEvent)
    });
  }

  /**
   * Generates a SOML schema from the provided payload.
   *
   * @param {GenerateSchemaModel} data the payload for generation
   * @returns {Promise<Schema>} promise resolving with a generated Schema
   */
  static generateSchema(data: GenerateSchemaModel): Promise<Schema> {
    return axios.post(SCHEMA, {
      vocPfx: data.vocPfx,
      id: data.id,
      label: data.label,
      stringMode: data.stringMode,
      lang: data.lang,
      files: data.files && data.files.map(file => file.path)
    }).then(this.resolveResponse)
      .then(SomlUtils.parseSchemaJsonLd)
      .catch(this.resolveErrorResponse);
  }

  /**
   * Deletes uploaded ontology file from the workbench server.
   *
   * @param {string} filePath the ontology file to delete
   * @returns {Promise<void>} promise resolving when the ontology has been deleted
   */
  static deleteOntoloy(filePath: string): Promise<void> {
    const encodedPath = encodeURIComponent(filePath);
    return axios.delete(`${ONTOLOGIES}/${encodedPath}`);
  }
}
